import { useMemo } from 'react';
import { uniq } from 'lodash';
import { PlaidTransaction } from '../types/transactions';
import { deriveIsSelf, normalizeMerchant } from '../utils/merchant-normalizer';
import { getVendorDisplayLabel } from '../utils/vendor-label';

export type GroupByOption =
  | 'merchant'
  | 'merchant_year'
  | 'vendor'
  | 'vendor_year'
  | 'category'
  | 'category_year'
  | 'subcategory'
  | 'subcategory_year'
  | 'month'
  | 'year';

export interface CheckEntry {
  /** Total amount of checks received (deposits) — always positive */
  in: number;
  /** Total amount of checks issued (payments) — always positive */
  out: number;
}

export interface MatrixData {
  columns: string[];
  rows: any[];
  colTotals: Record<string, number>;
  /** Check amounts per period + grand totals. Only populated when checkNumber is present. */
  checkData: {
    periods: Record<string, CheckEntry>;
    totals: CheckEntry;
  };
}

function looksLikeDescriptor(value: string): boolean {
  return /\b(online transfer|authorized on|ref\s*#|merchant name:|wire|deposit|checking|savings)\b/i.test(
    value,
  );
}

export function getMerchantLabelForMatrix(t: PlaidTransaction): string {
  if (deriveIsSelf(t)) return 'Self Transfer';

  const backendNormalized = (t.normalizedMerchant || '').trim();
  const backendTrusted =
    backendNormalized.length > 0 &&
    !!t.normalizationSource &&
    t.normalizationSource !== 'SYNC_LEGACY' &&
    !looksLikeDescriptor(backendNormalized);

  if (backendTrusted) return backendNormalized;

  const clientNormalized = normalizeMerchant(t).display.trim();
  if (clientNormalized) return clientNormalized;

  return t.merchantName || t.name || 'Unknown';
}


export function getVendorLabel(t: PlaidTransaction): string {
  return getVendorDisplayLabel(t);
}

export const useFinancialMatrix = (data: PlaidTransaction[], groupBy: GroupByOption): MatrixData => {
  return useMemo(() => {
    // Determine Period Length & Format
    const isYearly = groupBy === 'year' || groupBy === 'category_year' || groupBy === 'merchant_year' || groupBy === 'vendor_year';
    const periodLen = isYearly ? 4 : 7; // YYYY vs YYYY-MM

    // Get all unique periods for columns
    const allPeriods = uniq(data.map(t => t.date.substring(0, periodLen))).sort();
    
    // Rows
    const rowsMap: Record<string, any> = {};
    const checkAmounts: Record<string, CheckEntry> = {};

    data.forEach(t => {
        const period = t.date.substring(0, periodLen);
        const amount = t.amount || 0;
        let rowKey = '';
        
        if (groupBy === 'merchant' || groupBy === 'merchant_year') {
            const merchant = getMerchantLabelForMatrix(t);
            rowKey = merchant;
            if (!rowsMap[rowKey]) {
                rowsMap[rowKey] = {
                    type: 'merchant',
                    label: merchant,
                    total: 0,
                    periods: {}
                };
            }
        } else if (groupBy === 'vendor' || groupBy === 'vendor_year') {
            const vendor = getVendorLabel(t);
            rowKey = vendor;
            if (!rowsMap[rowKey]) {
                rowsMap[rowKey] = {
                    type: 'vendor',
                    label: vendor,
                    total: 0,
                    periods: {}
                };
            }
        } else if (groupBy === 'category' || groupBy === 'category_year') {
            // Category (PFC)
            const cat = t.personalFinanceCategoryPrimary || 'Uncategorized';
            const sub = t.personalFinanceCategoryDetailed || 'Uncategorized';
            rowKey = `${cat}::${sub}`;
            if (!rowsMap[rowKey]) {
                rowsMap[rowKey] = {
                    type: 'category',
                    category: cat,
                    subcategory: sub,
                    total: 0,
                    periods: {}
                };
            }
        } else if (groupBy === 'month' || groupBy === 'year') {
             // Time-only grouping -> Separate Income and Expense Rows
             // Plaid convention: amount > 0 = debit (expense), amount < 0 = credit (income)
             if (amount < 0) {
                 rowKey = 'income';
                 if (!rowsMap[rowKey]) {
                    rowsMap[rowKey] = {
                        type: 'summary',
                        label: 'Income',
                        total: 0,
                        periods: {}
                    };
                 }
             } else if (amount > 0) {
                 rowKey = 'expense';
                 if (!rowsMap[rowKey]) {
                    rowsMap[rowKey] = {
                        type: 'summary',
                        label: 'Expense',
                        total: 0,
                        periods: {}
                    };
                 }
             }
        }

        if (rowsMap[rowKey]) {
            // For month/year income row, use absolute value so income displays as positive
            const effectiveAmount = (groupBy === 'month' || groupBy === 'year') && rowKey === 'income'
                ? Math.abs(amount)
                : amount;
            rowsMap[rowKey].total += effectiveAmount;
            rowsMap[rowKey].periods[period] = (rowsMap[rowKey].periods[period] || 0) + effectiveAmount;
        }

        // Accumulate check amounts separately (stored as always-positive)
        if (t.checkNumber) {
            if (!checkAmounts[period]) checkAmounts[period] = { in: 0, out: 0 };
            if (amount < 0) {
                // Plaid credit (amount < 0) = money coming in = check deposit
                checkAmounts[period].in += Math.abs(amount);
            } else if (amount > 0) {
                // Plaid debit (amount > 0) = money going out = check payment
                checkAmounts[period].out += amount;
            }
        }
    });

    // Convert to Array and Sort
    const rows = Object.values(rowsMap).sort((a, b) => {
        // For time-only grouping (month/year), sort Income first, then Expense
        if ((groupBy === 'month' || groupBy === 'year') && a.type === 'summary' && b.type === 'summary') {
            if (a.label === 'Income' && b.label === 'Expense') return -1;
            if (a.label === 'Expense' && b.label === 'Income') return 1;
        }
        // For merchant/category grouping: INCOME rows first alphabetically, then EXPENSE alphabetically.
        // FinancialPivot negates all non-Income-summary rows for display, so after negation income = positive.
        // Plaid convention: negative amount = credit = income → after negation (×-1) becomes positive.
        const aIsIncome = a.total * -1 > 0; // i.e. a.total < 0
        const bIsIncome = b.total * -1 > 0;
        if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
        // Within same group (both income or both expense), sort alphabetically by label
        const aLabel = a.label || a.category || '';
        const bLabel = b.label || b.category || '';
        return aLabel.localeCompare(bLabel);
    });

    // Calculate Column Totals
    const colTotals: Record<string, number> = {};
    allPeriods.forEach(p => colTotals[p] = 0);
    colTotals.grandTotal = 0;

    const isSummaryMode = groupBy === 'month' || groupBy === 'year';
    const isVendorMode = groupBy === 'vendor' || groupBy === 'vendor_year';

    rows.forEach(row => {
        if (isSummaryMode && row.type === 'summary') {
            // Net = Income - Expenses (both stored as positive values)
            const sign = row.label === 'Income' ? 1 : -1;
            colTotals.grandTotal += sign * row.total;
            allPeriods.forEach(p => {
                if (row.periods[p]) colTotals[p] += sign * row.periods[p];
            });
        } else {
            colTotals.grandTotal += row.total;
            allPeriods.forEach(p => {
                if (row.periods[p]) colTotals[p] += row.periods[p];
            });
        }
    });

    // In non-summary modes, amounts follow Plaid convention (positive = expense, negative = income).
    // Negate colTotals so the footer shows net income − expenses:
    //   positive  → income > expenses (net income)
    //   negative  → expenses > income (net expense)
    if (!isSummaryMode) {
        Object.keys(colTotals).forEach(k => {
            colTotals[k] = -colTotals[k];
        });
    }

    // Check grand totals
    const checkTotals: CheckEntry = { in: 0, out: 0 };
    for (const entry of Object.values(checkAmounts)) {
        checkTotals.in += entry.in;
        checkTotals.out += entry.out;
    }

    return { columns: allPeriods, rows, colTotals, checkData: { periods: checkAmounts, totals: checkTotals } };
  }, [data, groupBy]);
};
